import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useApp } from "@/lib/store";
import { FlaskConical, Package, Calculator, Heart, Award, Settings } from "lucide-react";

export default function MyLab() {
  const { user } = useApp();
  const [tab, setTab] = useState("overview");
  const [orders, setOrders] = useState([]);
  const [calcs, setCalcs] = useState([]);

  useEffect(() => {
    api.get("/orders").then(({ data }) => setOrders(data)).catch(() => {});
    api.get("/calc/list").then(({ data }) => setCalcs(data)).catch(() => {});
  }, []);

  const tabs = [
    ["overview", "Overview", FlaskConical], ["orders", "Orders", Package],
    ["calcs", "Saved Calculations", Calculator], ["favorites", "Favorites", Heart],
    ["rewards", "Rewards", Award], ["account", "Account", Settings],
  ];

  return (
    <div className="px-4 sm:px-8 lg:px-12 pt-28 pb-20 max-w-7xl mx-auto" data-testid="patient-dashboard">
      <div className="mono-label mb-3">Customer dashboard</div>
      <h1 className="font-serif text-5xl text-white mb-2">My Lab</h1>
      <p className="text-sm text-slate-400 mb-10">Welcome back, <span className="text-cyan-300 font-mono">{user?.name}</span>.</p>

      <Link to="/my-program" data-testid="mylab-program-banner"
        className="block glass rounded-2xl p-6 mb-8 border border-cyan-400/20 hover:border-cyan-400/50 transition-colors">
        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="mono-label mb-1">CG Performance OS</div>
            <div className="font-serif text-2xl text-white">My Program</div>
            <p className="text-sm text-slate-400 mt-1">Your adaptive AI-assisted performance program — nutrition, training, coaching & progress.</p>
          </div>
          <span className="px-6 py-2.5 rounded-full btn-primary text-[11px] shrink-0">Open</span>
        </div>
      </Link>

      <div className="flex flex-wrap gap-1 border-b border-white/10 mb-8">
        {tabs.map(([k, l, I]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`mylab-tab-${k}`}
            className={`px-4 py-2.5 text-xs font-mono uppercase tracking-widest flex items-center gap-2 rounded-t-xl transition-colors ${tab === k ? "text-cyan-300 border-b-2 border-cyan-400" : "text-slate-500 hover:text-slate-300"}`}>
            <I size={13} /> {l}
          </button>
        ))}
      </div>

      {tab === "overview" && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[["Orders", orders.length], ["Saved Calculations", calcs.length], ["Reward Points", user?.reward_points || 0], ["Role", user?.role]].map(([l, v]) => (
            <div key={l} className="glass rounded-2xl p-5"><div className="font-mono text-2xl text-cyan-300">{v}</div><div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mt-1">{l}</div></div>
          ))}
        </div>
      )}

      {tab === "orders" && (
        <div className="space-y-3">
          {orders.length === 0 && <div className="glass rounded-2xl p-10 text-center text-slate-500 text-sm">No orders yet. <Link to="/shop" className="text-cyan-300">Browse the shop</Link>.</div>}
          {orders.map((o) => (
            <div key={o.id} className="glass rounded-2xl p-5" data-testid={`order-${o.order_number}`}>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div><div className="font-mono text-sm text-white">{o.order_number}</div><div className="text-[11px] font-mono text-slate-500 mt-0.5">{o.created_at?.slice(0, 10)}</div></div>
                <div className="font-mono text-cyan-300">${o.total.toFixed(2)}</div>
                <span className="stat-pill">{o.status}</span>
              </div>
              <div className="mt-3 text-xs font-mono text-slate-500">{o.items?.length} item(s) · Bulk tier: {o.bulk_tier}</div>
            </div>
          ))}
        </div>
      )}

      {tab === "calcs" && (
        <div className="space-y-3">
          {calcs.length === 0 && <div className="glass rounded-2xl p-10 text-center text-slate-500 text-sm">No saved calculations. <Link to="/tools/calculator" className="text-cyan-300">Open the Precision Calculator</Link>.</div>}
          {calcs.map((c) => (
            <div key={c.id} className="glass rounded-2xl p-5 flex items-center justify-between" data-testid={`calc-save-${c.id}`}>
              <div><div className="text-sm text-white">{c.label}</div><div className="text-[11px] font-mono text-slate-500 mt-1">{c.vial_mg}mg / {c.diluent_ml}mL → {c.result?.mgPerMl?.toFixed(3)} mg/mL</div></div>
              <span className="text-[10px] font-mono text-slate-500">{c.created_at?.slice(0, 10)}</span>
            </div>
          ))}
        </div>
      )}

      {tab === "favorites" && <div className="glass rounded-2xl p-10 text-center text-slate-500 text-sm">Research lists and saved peptides will appear here.</div>}
      {tab === "rewards" && (
        <div className="glass rounded-2xl p-8 max-w-lg">
          <div className="mono-label mb-3">CG Labz Research Rewards</div>
          <div className="font-mono text-4xl text-cyan-300">{user?.reward_points || 0} pts</div>
          <p className="text-xs text-slate-500 mt-3">Earn points on purchases, referrals and approved reviews. Redeem for discounts and exclusive access.</p>
        </div>
      )}
      {tab === "account" && (
        <div className="glass rounded-2xl p-6 max-w-lg space-y-3 font-mono text-sm">
          <div className="flex justify-between border-b border-white/5 pb-2"><span className="text-slate-500">Name</span><span>{user?.name}</span></div>
          <div className="flex justify-between border-b border-white/5 pb-2"><span className="text-slate-500">Email</span><span>{user?.email}</span></div>
          <div className="flex justify-between border-b border-white/5 pb-2"><span className="text-slate-500">Role</span><span className="text-cyan-300">{user?.role}</span></div>
          {user?.partner_code && <div className="flex justify-between border-b border-white/5 pb-2"><span className="text-slate-500">Partner code</span><span className="text-cyan-300">{user.partner_code}</span></div>}
        </div>
      )}
    </div>
  );
}
